"use client";

import React from "react";
import {PageCompetitionBookSchema} from "@/sanity/pages/PageCompetitionBookTemplate";

import Image from "next/image";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import DividerGreenLine from "@/components/global/ui/DividerGreenLine";
import ChipScrollingBanner from "@/components/global/ui/ChipScrollingBanner";
import {CTACornerButton} from "@/shadcn/ui/corner-button";

export default function IntroSection({
  data,
  className,
}: {
  data: PageCompetitionBookSchema;
  className?: string;
}) {
  return (
    <section className={cn(
      "relative w-full bg-[#0A1A1D] pt-[12rem] lg:pt-[20rem] overflow-clip",
      className
    )}>
      <div className="container pb-[12rem] lg:pb-[20rem]">
        <div className="w-[min(183rem,100%)] mx-auto flex flex-col items-center gap-[12rem] lg:gap-[18rem]">
          <div className="w-full flex flex-col lg:flex-row-reverse items-center lg:justify-between gap-[4rem]">
            {data?.intro1Image && (
              <Image
                className="!w-[min(97.25rem,100%)] flex-shrink-0"
                src={data.intro1Image}
                alt=""
              />
            )}

            <div className="w-[min(71.75rem,100%)] flex flex-col items-center lg:items-start">
              {data?.intro1Title && (
                <div className="richtext lg:richtext-start prose-headings:text-white/60 prose-strong:text-white lg:prose-headings:text-[6rem] prose-headings:tracking-normal prose-strong:font-light prose-headings:font-light prose-headings:!font-ivy prose-headings:leading-none prose-headings:pb-[3rem]">
                  <PortableText
                    value={data.intro1Title}
                    components={richTextComponent}
                  />
                </div>
              )}

              <DividerGreenLine
                className="w-[min(89.5rem,100%)] mb-[3rem]"
                greenLineClassName="lg:left-0 lg:translate-x-0"
              />

              {data?.intro1Text && (
                <div className="richtext lg:richtext-start prose-p:text-white/60 prose-p:font-light prose-strong:text-white prose-strong:font-light">
                  <PortableText
                    value={data.intro1Text}
                    components={richTextComponent}
                  />
                </div>
              )}

              {data?.intro1CTA && (
                <CTACornerButton
                  className="[&_button]:!text-[2rem] lg:[&_button]:!text-[2.25rem] mt-[3rem]"
                  data={data.intro1CTA}
                />
              )}
            </div>
          </div>

          <div className="w-full flex flex-col lg:flex-row items-center lg:justify-between gap-[4rem]">
            {data?.intro2Image && (
              <Image
                className="!w-[35rem] sm:!w-[45rem] lg:!w-[min(71rem,100%)] flex-shrink-0"
                src={data.intro2Image}
                alt=""
              />
            )}

            <div className="w-[min(90.25rem,100%)] flex flex-col items-start">
              {data?.intro2Title && (
                <div className="richtext richtext-start prose-headings:text-white prose-strong:text-white prose-p:text-white lg:prose-headings:text-[6rem] prose-headings:tracking-normal prose-strong:font-light prose-headings:font-light prose-headings:!font-ivy prose-headings:leading-none prose-headings:pb-[1rem] prose-headings:mb-0">
                  <PortableText
                    value={data.intro2Title}
                    components={richTextComponent}
                  />
                </div>
              )}

              <DividerGreenLine
                className="w-[min(89.5rem,100%)] my-[3rem]"
                greenLineClassName="left-0 translate-x-0"
              />

              {data?.intro2Text && (
                <div className="richtext richtext-start prose-p:text-white/60 prose-p:font-light prose-strong:text-white prose-strong:font-light mb-[3rem]">
                  <PortableText
                    value={data.intro1Text}
                    components={richTextComponent}
                  />
                </div>
              )}

              {data?.intro2Text2 && (
                <div className="sm:bg-[#D9F9B9]/5 sm:border-l sm:border-[#D9F9B9] sm:px-[2.5rem] sm:py-[1.5rem] richtext richtext-start prose-headings:text-white prose-headings:text-[2.25rem] prose-headings:!font-geist prose-headings:font-normal prose-p:text-white/60 prose-p:font-light prose-strong:text-white prose-strong:font-light mb-[3rem] prose-li:text-white/70 prose-ul:mb-[2rem] prose-ul:flex prose-ul:flex-col prose-ul:gap-[1rem] prose-ul:pl-[2.5rem] prose-ul:list-disc prose-li:marker:text-[#D9F9B9] prose-li:m-0">
                  <PortableText
                    value={data.intro2Text2}
                    components={richTextComponent}
                  />
                </div>
              )}

              {data?.intro2Text3 && (
                <div className="richtext richtext-start prose-p:text-white/60 prose-p:font-light prose-strong:text-white prose-strong:font-light">
                  <PortableText
                    value={data.intro2Text3}
                    components={richTextComponent}
                  />
                </div>
              )}

              {data?.intro2CTA && (
                <CTACornerButton
                  className="[&_button]:!text-[2rem] lg:[&_button]:!text-[2.25rem] mt-[3rem]"
                  data={data.intro2CTA}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <ChipScrollingBanner
        texts={data.intro2Chips}
        speed="fast"
        direction="backward"
      />
    </section>
  );
}
